<script lang="ts">
  import { slide } from "svelte/transition"
  import { ChevronDown, ChevronRight } from "lucide-svelte"

  import Questions from "../Questions/Index.svelte"

  let props = $props()
  let { questions, response } = props
  let pdf_mode = $state(props.pdf_mode ?? false)

  let expand_details = $state(false)

  let alignment_questions = Object.entries(questions).filter(
    ([step, q]) => q.category === "trust"
  )

  function answer_letter(i: number): string {
    if (i == 1) {
      return "A"
    }
    if (i == 2) {
      return "B"
    }
    if (i == 3) {
      return "C"
    }
    if (i == 4) {
      return "D"
    }
  }
</script>

<div class="rounded-lg border-gray-200 lg:mt-8" class:border={!pdf_mode}>
  <h3
    class="text-lg lg:text-2xl font-semibold sm:font-bold text-black w-full py-3 px-5 bg-blue-900 text-white"
  >
    {#if response.score.trust < 25}
      3. TRUST: It's Broken, or Missing Entirely
    {:else if response.score.trust >= 25 && response.score.trust < 50}
      3. TRUST: It’s Talked About, But Not Practiced
    {:else if response.score.trust >= 50 && response.score.trust < 80}
      3. TRUST
    {:else if response.score.trust >= 80 && response.score.trust < 100}
      3. TRUST IN EVERY DIRECTION
    {:else if response.score.trust === 100}
      3. TRUST MULTIPLIERS
    {/if}
  </h3>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-6 p-4 lg:p-6">
    {#if response.score.trust < 25}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          Your people don’t speak up. They don’t challenge ideas. They don’t
          lead without permission.
        </p>

        <p class="mt-2 lg:mt-6 text-base lg:text-lg/8 text-gray-600 font-bold">
          That’s not discipline - that’s fear or disengagement.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          If no one’s pushing back, they’re not really bought in. Silence is
          your red flag.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-xl/8 font-bold text-gray-700">The consequences:</p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 text-sm sm:text-base/7 text-gray-600">
            Problems get hidden, not solved
          </li>
          <li class="mt-2 lg:mt-4 text-sm sm:text-base/7 text-gray-600">
            Accountability is surface-level at best
          </li>
          <li class="mt-2 lg:mt-4 text-sm sm:text-base/7 text-gray-600">
            You’re left guessing what’s really going on
          </li>
        </ul>
      </div>
    {:else if response.score.trust >= 25 && response.score.trust < 50}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          People may be respectful. Even friendly. But that doesn’t mean they
          feel safe taking initiative, pushing back, or owning big decisions.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base sm:text-lg/8">
          When people don’t speak up, they don’t step up. Silence is not
          alignment.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-xl/8 font-bold text-gray-700">
          Symptoms of low trust:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 text-sm sm:text-base/7 text-gray-600">
            Meetings are quiet or surface-level
          </li>
          <li class="mt-2 lg:mt-4 text-sm sm:text-base/7 text-gray-600">
            Junior leaders hesitate in high-stakes moments
          </li>
          <li class="mt-2 lg:mt-4 text-sm sm:text-base/7 text-gray-600">
            You find out about problems after they’ve escalated
          </li>
        </ul>
      </div>
    {:else if response.score.trust >= 50 && response.score.trust < 80}
      <div>
        <p class="text-base lg:text-lg/8 font-bold text-gray-700">
          Trust Might Be Being Talked About, But Not Practiced
        </p>
        <p class="mt-2 lg:mt-6 text-base lg:text-lg/8 text-gray-600">
          Psychological safety isn’t about beanbags and open-door policies. It’s
          about your team’s ability to disagree, speak up, and make calls -
          without fear.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          The gaps we often see:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            Crucial conversations don’t happen - or happen after the damage is
            done
          </li>
          <li class="mt-2 sm:mt-4 text-sm lg:text-base/7 text-gray-600">
            Junior leaders hesitate instead of leading
          </li>
          <li class="mt-2 sm:mt-4 text-sm lg:text-base/7 text-gray-600">
            Meetings are performative, not productive
          </li>
          <li class="mt-2 sm:mt-4 text-sm lg:text-base/7 text-gray-600">
            Silence gets mistaken for alignment
          </li>
        </ul>
      </div>
    {:else if response.score.trust >= 80 && response.score.trust < 100}
      <div>
        <p class="text-base lg:text-lg/8 text-gray-600">
          You trust your team. But do they trust each other? Are they
          comfortable pushing back, sharing half-formed ideas, and stepping into
          ambiguity?
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Even elite teams need reps in:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            Calling each other up, not just out
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Disagreeing productively
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Leading laterally, not just vertically
          </li>
        </ul>
      </div>
    {:else if response.score.trust == 100}
      <div>
        <p class="text-base lg:text-xl/8 font-bold text-gray-700">
          You’ve built trust - now it needs to multiply.
        </p>

        <p class="mt-2 lg:mt-6 italic text-base lg:text-lg/8">
          The next challenge isn’t your confidence in the team. It’s creating an
          environment where they push each other, challenge ideas, and own
          outcomes laterally.
        </p>
      </div>
      <div class="bg-gray-50 py-2 px-2 lg:py-4 sm:px-4">
        <p class="mt-2 text-base lg:text-lg/8 font-bold text-gray-700">
          Elite teams are forged in:
        </p>
        <ul class="list-disc ml-6 sm:ml-8">
          <li class="mt-2 text-sm lg:text-base/7 text-gray-600">
            Productive conflict
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Shared ownership
          </li>
          <li class="mt-2 lg:mt-4 text-sm lg:text-base/7 text-gray-600">
            Fast recovery from failure
          </li>
        </ul>
      </div>
    {/if}
  </div>

  <div>
    {#if pdf_mode}
      <p class="text-xl font-bold text-gray-900 mt-2 lg:mt-6">
        Detailed question analysis
      </p>

      <div class="py-2 grid grid-cols-1 gap-y-2">
        {#each alignment_questions as [step, question]}
          {@const response_answer = response.response_answers.find(
            (ra) => ra.step == step
          )}
          <div class="p-4" style="break-after: page;">
            <p class="text-xl text-center text-blue-900 font-bold">
              Question {step}
            </p>
            <p class="text-lg text-gray-700 font-bold mt-4">
              {question.question}
            </p>

            <div class="mx-auto max-w-xl mt-6">
              {#each Object.entries(question.answers) as [i, answer]}
                <p
                  class="text-sm text-gray-600 py-1 px-2 flex"
                  class:font-bold={response_answer.answer == i}
                  class:bg-blue-100={response_answer.answer == i}
                >
                  {answer_letter(i)})
                  {answer}
                </p>
              {/each}
            </div>
            <p class="text-sm text-gray-700 font-semibold italic mt-6">
              Analysis of your answer:
            </p>
            <div class="col-span-2 mt-2">
              <Questions question={step} answer={response_answer.answer} />
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <button
        class="w-full text-base lg:text-lg px-4 py-3 border-t border-gray-200 bg-gray-50 hover:bg-gray-100 hover:text-blue-700 text-left font-bold flex items-center justify-start"
        class:bg-gray-100={expand_details}
        class:text-blue-700={expand_details}
        onclick={() => (expand_details = !expand_details)}
      >
        {#if expand_details}
          <ChevronDown class="size-5 lg:size-6" />
        {:else}
          <ChevronRight class="size-5 lg:size-6" />
        {/if}
        View Detailed Question Analysis</button
      >

      {#if expand_details}
        <div
          transition:slide={{ duration: 600 }}
          class="px-2 sm:px-4 py-2 sm:py-4 grid grid-cols-1 gap-y-4 sm:gap-y-6"
        >
          {#each alignment_questions as [step, question]}
            {@const response_answer = response.response_answers.find(
              (ra) => ra.step == step
            )}
            <div
              class="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-gray-50 border border-gray-100 rounded p-2 sm:p-4"
            >
              <div>
                <p
                  class="text-base sm:text-lg/8 lg:text-xl/8 text-gray-700 font-bold"
                >
                  {step}. {question.question}
                </p>
              </div>
              <div>
                {#each Object.entries(question.answers) as [i, answer]}
                  <p
                    class="text-sm sm:text-base/8 text-gray-600 py-1 px-2"
                    class:font-bold={response_answer.answer == i}
                    class:bg-blue-100={response_answer.answer == i}
                  >
                    {answer_letter(i)})
                    {answer}
                  </p>
                {/each}
              </div>

              <div class="col-span-1 lg:col-span-2">
                <Questions question={step} answer={response_answer.answer} />
              </div>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
</div>
